import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, FolderKanban, Users, Clock, Shield, TrendingUp, Settings,
  Receipt, Wallet, Building2, FileText, CalendarDays, LogOut,
  FileBarChart, Bookmark, Crown, Home, ChevronDown, ChevronRight,
  FileSignature, Sparkles, FileEdit, FilePlus, LineChart,
} from 'lucide-react';
import { useAuth } from '../lib/auth';

// Statuts :
//  - ready    : module stable
//  - beta     : V2 prévue (Facturation, Charges)
//  - upcoming : non développé — Phase future, item non cliquable

const ADMIN_SECTIONS = [
  {
    key: 'pilotage', title: 'Pilotage',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, status: 'ready', testid: 'sidebar-nav-dashboard' },
      { to: '/reporting', label: 'Reporting mensuel', icon: FileBarChart, status: 'ready', testid: 'sidebar-nav-reporting' },
      { to: '/analyse-temporelle', label: 'Analyse temporelle', icon: LineChart, status: 'ready', testid: 'sidebar-nav-analyse-temporelle',
        note: 'Courbe heures/euros par projet & filtres multi-critères' },
      { to: '/tresorerie', label: 'Trésorerie', icon: Wallet, status: 'ready', testid: 'sidebar-nav-tresorerie' },
    ],
  },
  {
    key: 'projets', title: 'Projets',
    items: [
      { to: '/projets', label: 'Tous les projets', icon: FolderKanban, status: 'ready', testid: 'sidebar-nav-projets' },
      { to: '/promoteurs', label: 'Clients', icon: Building2, status: 'ready', testid: 'sidebar-nav-promoteurs' },
      { to: '/rentabilite', label: 'Rentabilité', icon: TrendingUp, status: 'ready', testid: 'sidebar-nav-rentabilite' },
      { to: '/liens-utiles', label: 'Liens utiles', icon: Bookmark, status: 'ready', testid: 'sidebar-nav-liens-utiles',
        note: 'Bonnes pratiques partagées avec les salariés' },
    ],
  },
  {
    key: 'finance', title: 'Finance',
    items: [
      { to: '/factures', label: 'Facturation', icon: FileText, status: 'beta', testid: 'sidebar-nav-factures',
        note: 'V2 prévue — Phase 2 : factures multi-lignes + encaissements partiels' },
      { to: '/charges', label: 'Charges & Paiements', icon: Receipt, status: 'ready', testid: 'sidebar-nav-charges',
        note: 'Saisie des charges (déplacements, matériel, sous-traitance...) avec rattachement projet' },
      { to: '/encaissements', label: 'Encaissements', icon: Wallet, status: 'ready', testid: 'sidebar-nav-encaissements',
        note: 'Encaissements réels avec mode de paiement & référence' },
      { to: '/provisions', label: 'Assurance — Suivi', icon: Shield, status: 'ready', testid: 'sidebar-nav-provisions' },
      { to: '/remuneration-dirigeants', label: 'Rémun. dirigeants', icon: Crown, status: 'ready', testid: 'sidebar-nav-remu-dirig' },
    ],
  },
  {
    key: 'equipe', title: 'Équipe',
    items: [
      { to: '/salaries', label: 'Salariés', icon: Users, status: 'ready', testid: 'sidebar-nav-salaries' },
      { to: '/pointages', label: 'Pointages', icon: Clock, status: 'ready', testid: 'sidebar-nav-pointages' },
      { to: '/absences', label: 'Absences', icon: CalendarDays, status: 'ready', testid: 'sidebar-nav-absences' },
    ],
  },
  {
    key: 'outils', title: 'Outils & Documents',
    items: [
      { to: null, label: 'Devis', icon: FileEdit, status: 'upcoming', phase: 'P3' },
      { to: null, label: 'Génération contrat', icon: FileSignature, status: 'upcoming', phase: 'P4' },
      { to: null, label: 'Génération CERFA PC4', icon: FilePlus, status: 'upcoming', phase: 'P4' },
    ],
  },
  {
    key: 'config', title: 'Configuration',
    items: [
      { to: '/parametres', label: 'Paramètres', icon: Settings, status: 'ready', testid: 'sidebar-nav-parametres' },
    ],
  },
];

const SALARIE_ITEMS = [
  { to: '/', label: 'Mes pointages', icon: Clock, status: 'ready', testid: 'sidebar-nav-mes-pointages' },
  { to: '/mes-absences', label: 'Mes absences', icon: CalendarDays, status: 'ready', testid: 'sidebar-nav-mes-absences' },
  { to: '/projets-list', label: 'Liste projets', icon: FolderKanban, status: 'ready', testid: 'sidebar-nav-projets-list' },
  { to: '/liens-utiles', label: 'Liens utiles', icon: Bookmark, status: 'ready', testid: 'sidebar-nav-liens-utiles-sal' },
];

function StatusDot({ status }) {
  const map = {
    ready: { cls: 'bg-emerald-500', title: 'Module validé' },
    beta: { cls: 'bg-amber-400', title: 'Version 2 prévue' },
    upcoming: { cls: 'bg-zinc-600', title: 'À venir' },
  };
  const s = map[status] || map.ready;
  return <span title={s.title} className={`inline-block w-1.5 h-1.5 rounded-full ${s.cls}`} />;
}

const STORAGE_KEY = 'amn_sidebar_open_sections';

function loadOpenState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* noop */ }
  // Par défaut : tout ouvert
  return Object.fromEntries(ADMIN_SECTIONS.map(s => [s.key, true]));
}

function findSectionForPath(pathname) {
  for (const s of ADMIN_SECTIONS) {
    if (s.items.some(it => it.to && (pathname === it.to || pathname.startsWith(it.to + '/')))) return s.key;
  }
  return null;
}

export default function Sidebar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [openSections, setOpenSections] = useState(loadOpenState);

  // Auto-ouvrir la section qui contient l'item actif
  React.useEffect(() => {
    const k = findSectionForPath(location.pathname);
    if (k && !openSections[k]) {
      const next = { ...openSections, [k]: true };
      setOpenSections(next);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleSection = (k) => {
    const next = { ...openSections, [k]: !openSections[k] };
    setOpenSections(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* noop */ }
  };

  return (
    <aside className="w-64 bg-zinc-950 text-zinc-100 flex flex-col h-screen sticky top-0 border-r border-zinc-900">
      {/* Logo cliquable -> Hub */}
      <NavLink to="/" className="p-5 border-b border-zinc-900 flex items-center gap-3 hover:bg-zinc-900 transition-colors" data-testid="sidebar-logo">
        <div className="bg-white p-1.5 w-10 h-10 flex items-center justify-center">
          <img src="/assets/logo-mn.png" alt="MN" className="w-full h-full object-contain" />
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-sm tracking-tight truncate">ATELIER MN</div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em]">Architecture</div>
        </div>
      </NavLink>

      <nav className="flex-1 overflow-y-auto py-3">
        {isAdmin ? (
          <>
            {/* Lien rapide Hub */}
            <NavLink to="/" end
              className={({ isActive }) => `flex items-center gap-3 mx-3 px-3 py-2 text-sm border-l-2 ${isActive ? 'bg-zinc-900 text-white border-[#002FA7] font-medium' : 'text-zinc-300 border-transparent hover:bg-zinc-900 hover:text-white'}`}
              data-testid="sidebar-nav-hub">
              <Home size={15} strokeWidth={1.75} />
              <span className="flex-1">Accueil</span>
              <StatusDot status="ready" />
            </NavLink>

            {ADMIN_SECTIONS.map((section) => {
              const isOpen = !!openSections[section.key];
              return (
                <div key={section.key} className="mt-3" data-testid={`sidebar-section-${section.key}`}>
                  <button onClick={() => toggleSection(section.key)}
                    className="w-full flex items-center gap-2 px-5 py-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300 transition-colors"
                    data-testid={`sidebar-section-toggle-${section.key}`}>
                    {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                    <span className="flex-1 text-left">{section.title}</span>
                  </button>
                  {isOpen && (
                    <div className="mt-1">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const upcoming = item.status === 'upcoming';
                        if (upcoming) {
                          return (
                            <div key={item.label} title={item.note || ''}
                              className="flex items-center gap-3 px-7 py-2 text-sm text-zinc-600 border-l-2 border-transparent cursor-help select-none">
                              <Icon size={14} strokeWidth={1.5} className="opacity-60" />
                              <span className="flex-1 italic">{item.label}</span>
                              {item.phase && <span className="text-[9px] font-mono text-zinc-600 uppercase tracking-wider">{item.phase}</span>}
                              <StatusDot status="upcoming" />
                            </div>
                          );
                        }
                        return (
                          <NavLink key={item.to} to={item.to} data-testid={item.testid} title={item.note || ''}
                            className={({ isActive }) => `flex items-center gap-3 px-7 py-2 text-sm border-l-2 ${isActive ? 'bg-zinc-900 text-white border-[#002FA7] font-medium' : 'text-zinc-400 border-transparent hover:bg-zinc-900 hover:text-white'}`}>
                            <Icon size={14} strokeWidth={1.75} />
                            <span className="flex-1">{item.label}</span>
                            <StatusDot status={item.status} />
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Légende compacte */}
            <div className="mt-6 mx-4 px-3 py-2 border border-zinc-800 bg-zinc-900/40" data-testid="sidebar-legend">
              <div className="flex items-center gap-2 text-[9px] text-zinc-500 uppercase tracking-[0.15em] mb-1.5">
                <Sparkles size={10} /> Statuts
              </div>
              <div className="flex items-center gap-3 text-[9px] text-zinc-400 flex-wrap">
                <span className="inline-flex items-center gap-1"><StatusDot status="ready" />Validé</span>
                <span className="inline-flex items-center gap-1"><StatusDot status="beta" />V2 prévue</span>
                <span className="inline-flex items-center gap-1"><StatusDot status="upcoming" />À venir</span>
              </div>
            </div>
          </>
        ) : (
          <div className="mt-2">
            <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] px-7 py-2">Mon espace</div>
            {SALARIE_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink key={item.to} to={item.to} data-testid={item.testid}
                  className={({ isActive }) => `flex items-center gap-3 px-7 py-2.5 text-sm border-l-2 ${isActive ? 'bg-zinc-900 text-white border-[#002FA7] font-medium' : 'text-zinc-400 border-transparent hover:bg-zinc-900 hover:text-white'}`}>
                  <Icon size={16} strokeWidth={1.75} />
                  <span className="flex-1">{item.label}</span>
                  <StatusDot status={item.status} />
                </NavLink>
              );
            })}
          </div>
        )}
      </nav>

      <div className="p-3 border-t border-zinc-900">
        <div className="px-3 py-1 mb-1">
          <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em]">Connecté</div>
          <div className="text-sm font-medium text-white mt-0.5 truncate" data-testid="user-name">
            {user?.prenom} {user?.nom}
          </div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{user?.role}</div>
        </div>
        <button onClick={logout} data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-400 hover:bg-zinc-900 hover:text-white">
          <LogOut size={14} /> Déconnexion
        </button>
      </div>
    </aside>
  );
}
